import { Product } from '@commercetools/frontend-domain-types/product/Product';
import ProductSlider, { ProductSliderProps } from '@loomstateorg/shared-frontend/components/commercetools-ui/organisms/product/product-slider';
import { useMemo, useCallback } from 'react';
import useSanityPermissionsChecker from 'helpers/hooks/useSanityPermissionsChecker';
import { SustainabilityItem } from 'components/commercetools-ui/types';
import { TasticWithCMS } from 'types/tastic';
import { Reference } from 'types/reference';
import { Category } from '@commercetools/frontend-domain-types/product/Category';

export interface DataCmsProps {
  fields: SustainabilityItem;
}

/**
 * Normalize localized fields from Sanity format
 * Handles both string values and localized objects
 */
function normalizeLocalizedField(field: any): string {
  if (!field) return '';
  if (typeof field === 'string') return field;
  if (typeof field === 'object') {
    return field.en_US || field.enUS || field.en || Object.values(field)[0] || '';
  }
  return String(field);
}

/**
 * Normalize slug field from Sanity format
 * Supports multiple formats: string, { current: string }, or localized object
 */
function normalizeSlug(slugField: any, fallbackName?: string): string {
  if (!slugField) {
    // Generate slug from name if available
    if (fallbackName) {
      return fallbackName.toLowerCase().replaceAll(/\s+/g, '-').replaceAll(/[^a-z0-9-]/g, '');
    }
    return '';
  }

  if (typeof slugField === 'string') return slugField;

  if (typeof slugField === 'object') {
    // Sanity slug format: { current: "slug-value" }
    if (typeof slugField.current === 'string') return slugField.current;

    // Localized slug format: { en_US: "slug-value" }
    const localizedSlug = slugField.en_US || slugField.enUS || slugField.en;
    if (typeof localizedSlug === 'string') return localizedSlug;

    // Try to get first string value from object
    const firstValue = Object.values(slugField).find((v) => typeof v === 'string');
    if (firstValue) return firstValue;
  }

  return String(slugField);
}

/**
 * Generate product URL in format: /{slug}/p/{sku}
 */
function generateProductUrl(slug: string, sku: string): string {
  if (!sku) return '#';
  const urlSlug = slug || '';
  return `/${urlSlug}/p/${sku}`;
}

/**
 * Map products from Sanity format to CommerceTools Product format
 */
function mapSanityProductsToCommerceTools(sanityProducts: any[]): Product[] {
  if (!sanityProducts || !Array.isArray(sanityProducts)) {
    return [];
  }

  return sanityProducts.map((sanityProduct) => {
    const productName = normalizeLocalizedField(sanityProduct.name);
    const productSlug = normalizeSlug(sanityProduct.slug, productName);
    const firstVariantSku = sanityProduct.variants?.[0]?.sku || '';

    const categories: Category[] = (sanityProduct.categories || []).map((cat: any) => ({
      categoryId: cat.id,
    }));

    return {
      productId: sanityProduct.id,
      name: productName as any,
      description: normalizeLocalizedField(sanityProduct.description) as any,
      slug: productSlug as any,
      categories,
      variants: sanityProduct.variants || [],
      attributes: sanityProduct.attributes || {},
      _url: generateProductUrl(productSlug, firstVariantSku),
    } as Product;
  });
}

/**
 * Extract slider properties from component data
 */
function extractSliderProperties(component: any): ProductSliderProps {
  const url = component?.url;
  let ctaLink: Reference | undefined;

  if (url?.linkUrl) {
    ctaLink = {
      type: 'link',
      link: url.linkUrl,
      openInNewWindow: url.openInNewTab || false,
    } as Reference;
  }

  return {
    title: component?.title,
    subline: component?.subtitle,
    ctaLabel: url?.linkName,
    ctaLink,
  } as ProductSliderProps;
}

function ProductFeaturedSliderTastic({
  data,
  customerGroup,
}: Readonly<{
  data: TasticWithCMS<any, unknown, DataCmsProps>['data'] & {
    cmsFeaturedProducts?: any;
    cmsCertifications?: any;
    cmsEnvironmental?: any;
  };
  customerGroup: any;
}>) {
  const { checkNestedItemPermission } = useSanityPermissionsChecker();
  // Helper: Check permissions only at the current level (without checking nested children)
  const checkTopLevelPermission = useCallback(
    (item: any, customerGroup: any) => {
      if (!item) return false;
      // Create a copy without icons to check only parent level permissions
      const { icons, ...itemWithoutIcons } = item;
      return checkNestedItemPermission(itemWithoutIcons, customerGroup);
    },
    [checkNestedItemPermission],
  );

  // Get certifications from data.cmsCertifications (Sanity structure: dataSource.fields)
  const cmsCertificationsDataSource = (data as any)?.cmsCertifications?.dataSource;
  const cmsCertifications = cmsCertificationsDataSource?.fields;

  // Get environmental from data.cmsEnvironmental (Sanity structure: dataSource.fields)
  const cmsEnvironmentalDataSource = (data as any)?.cmsEnvironmental?.dataSource;
  const cmsEnvironmental = cmsEnvironmentalDataSource?.fields;

  // Get all components that the user has permission to see (not just the first one)
  const allowedComponents = useMemo(() => {
    const cmsFeaturedProducts = (data as any)?.cmsFeaturedProducts;
    const dataSource = cmsFeaturedProducts?.dataSource;
    const fields = dataSource?.fields;

    // Check top-level permissions first (only check fields.userRoles, not components)
    if (fields?.userRoles) {
      const fieldsOnly = { userRoles: fields.userRoles };
      if (!checkNestedItemPermission(fieldsOnly, customerGroup)) {
        return [];
      }
    }

    // Get all components
    const components = fields?.components as any[];
    if (!components || components.length === 0) {
      return [];
    }

    // Return all components that pass the permission check (not just the first)
    return components.filter((component: any) => checkNestedItemPermission(component, customerGroup));
  }, [data, customerGroup, checkNestedItemPermission]);

  // For each allowed component: products + slider properties (computed once per component)
  const slidersData = useMemo(() => {
    return allowedComponents.map((component: any) => {
      const commerceToolsProduct = component?.commerceToolsProduct;
      const categoryData = commerceToolsProduct?.categoryData;
      const sanityProducts = categoryData?.products || [];
      const products = mapSanityProductsToCommerceTools(sanityProducts);
      const properties = extractSliderProperties(component);
      return { products, properties };
    });
  }, [allowedComponents]);

  const sustainability = useMemo(() => {
    // Check permissions for certifications at top level (without checking icons)
    const hasCertTopLevelPermission = checkTopLevelPermission(cmsCertifications, customerGroup);

    // Filter icons by individual permissions (only if top level has permission)
    const filteredCertIcons =
      hasCertTopLevelPermission && cmsCertifications
        ? (cmsCertifications?.icons || []).filter((icon: any) => checkNestedItemPermission(icon, customerGroup))
        : [];

    const certificationsItem =
      cmsCertifications && hasCertTopLevelPermission && filteredCertIcons.length > 0
        ? {
            ...cmsCertifications,
            icons: filteredCertIcons,
            shouldShowIcons: !!cmsCertifications?.shouldShowIcons,
          }
        : undefined;

    // Check permissions for environmental at top level (without checking icons)
    const hasEnvTopLevelPermission = checkTopLevelPermission(cmsEnvironmental, customerGroup);

    // Filter icons by individual permissions (only if top level has permission)
    const filteredEnvIcons =
      hasEnvTopLevelPermission && cmsEnvironmental
        ? (cmsEnvironmental?.icons || []).filter((icon: any) => checkNestedItemPermission(icon, customerGroup))
        : [];

    const environmentalItem =
      cmsEnvironmental && hasEnvTopLevelPermission && filteredEnvIcons.length > 0
        ? {
            ...cmsEnvironmental,
            icons: filteredEnvIcons,
            shouldShowIcons: !!cmsEnvironmental?.shouldShowIcons,
          }
        : undefined;

    return {
      certificationsItem: certificationsItem as SustainabilityItem | undefined,
      environmentalItem: environmentalItem as SustainabilityItem | undefined,
    };
  }, [cmsCertifications, cmsEnvironmental, customerGroup, checkNestedItemPermission, checkTopLevelPermission]);

  // Don't render if no component has permission
  if (!allowedComponents.length) {
    return null;
  }

  // Render one ProductSlider per allowed section that has products
  return (
    <>
      {slidersData.map(({ products, properties }, index) => {
        if (products.length === 0) {
          return null;
        }
        return (
          <ProductSlider
            key={index}
            {...properties}
            products={products}
            sustainability={sustainability}
            dataTestId="featured"
          />
        );
      })}
    </>
  );
}

export default ProductFeaturedSliderTastic;
